import type { SearchResultsBlockProps, Listing } from '../types';
import { theme, sectionColors } from '../theme';

const MARKER_COLOR = '4E1F60';
const DEFAULT_POSITION = '-80.4,27.2,5.5';
const MAX_MARKERS = 20;

function formatPrice(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/** Presentational filter bar — the search-page chrome (real filtering is the
 *  tenant /listings route's job; this gives the layout + variant look). */
function FilterBar({ compact }: { compact: boolean }) {
  const pill =
    'rounded-full border border-black/15 px-4 py-1.5 text-sm whitespace-nowrap';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-full border border-black/15">
        <span className="px-4 py-1.5 text-sm font-medium" style={{ backgroundColor: theme.primary, color: theme.onPrimary }}>
          Buy
        </span>
        <span className="px-4 py-1.5 text-sm">Rent</span>
        <span className="px-4 py-1.5 text-sm">Sold</span>
      </div>
      <span className={pill}>Price</span>
      <span className={pill}>Beds</span>
      <span className={pill}>Baths</span>
      {!compact ? (
        <>
          <span className={pill}>Property Type</span>
          <span className={pill}>More Filters</span>
        </>
      ) : null}
      <span
        className="ml-auto rounded-md px-4 py-1.5 text-sm font-semibold text-white"
        style={{ backgroundColor: theme.primary }}
      >
        Search
      </span>
    </div>
  );
}

/**
 * Pure split-panel search results — a listings column beside a map, with the map
 * on the left or right (responsive: stacks on mobile, results first). Reuses the
 * SAME host-provided data as the listings + map blocks (no new fetch wiring).
 */
export function SearchResultsBlock(
  props: SearchResultsBlockProps & { listings?: Listing[]; mapboxToken?: string | null },
) {
  const {
    heading,
    mapPosition = 'right',
    filterStyle = 'bar',
    limit,
    listings = [],
    mapboxToken,
  } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  const items = typeof limit === 'number' ? listings.slice(0, limit) : listings;

  const points = listings
    .map((l) => ({ lat: l.address?.latitude, lng: l.address?.longitude }))
    .filter(
      (p): p is { lat: number; lng: number } =>
        typeof p.lat === 'number' && Number.isFinite(p.lat) &&
        typeof p.lng === 'number' && Number.isFinite(p.lng),
    )
    .slice(0, MAX_MARKERS);

  const mapImg = mapboxToken
    ? (() => {
        const overlay = points.length
          ? `${points
              .map((p) => `pin-s+${MARKER_COLOR}(${p.lng.toFixed(5)},${p.lat.toFixed(5)})`)
              .join(',')}/auto`
          : DEFAULT_POSITION;
        const src = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${overlay}/800x1000@2x?access_token=${encodeURIComponent(
          mapboxToken,
        )}`;
        return (
          <img
            src={src}
            alt={heading || 'Property map'}
            loading="lazy"
            decoding="async"
            className="h-64 w-full rounded-lg border border-black/10 object-cover lg:h-full lg:min-h-[600px]"
          />
        );
      })()
    : (
        <div className="flex h-64 items-center justify-center rounded-lg border border-black/10 bg-black/[0.04] text-sm text-black/50 lg:h-full lg:min-h-[600px]">
          Map appears here once a Mapbox token is connected.
        </div>
      );

  // Map column order: 'left' → map first on desktop; mobile always shows results
  // first (map drops below) so the listings are immediately scannable on phones.
  const mapOrder = mapPosition === 'left' ? 'lg:order-1' : 'lg:order-2';
  const resultsOrder = mapPosition === 'left' ? 'lg:order-2' : 'lg:order-1';

  const resultsCol = (
    <div className={`flex-1 ${resultsOrder}`}>
      {items.length === 0 ? (
        <p className="py-16 text-center opacity-60">No listings available right now.</p>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {items.map((l) => (
            <a
              key={l.listing_key}
              href={`/listings/${encodeURIComponent(l.listing_key)}`}
              className="block overflow-hidden rounded-lg border border-black/10 transition hover:shadow-md"
            >
              {l.photos?.[0] ? (
                <img src={l.photos[0]} alt={l.address.street} loading="lazy" decoding="async" className="h-40 w-full object-cover" />
              ) : null}
              <div className="p-3">
                <div className="text-base font-semibold">{formatPrice(l.list_price)}</div>
                <div className="text-sm">{l.address.street}</div>
                <div className="text-xs opacity-70">
                  {l.address.city}, {l.address.state} {l.address.zip}
                </div>
                <div className="mt-1 text-xs opacity-70">
                  {l.beds} bd · {l.baths_total} ba{l.sqft ? ` · ${l.sqft.toLocaleString()} sqft` : ''}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );

  const mapCol = <div className={`lg:w-[40%] ${mapOrder}`}>{mapImg}</div>;

  return (
    <section className="px-6 py-8" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-7xl">
        {heading ? (
          <h2 className="mb-4 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
            {heading}
          </h2>
        ) : null}
        {filterStyle !== 'none' ? (
          <div className="mb-6">
            <FilterBar compact={filterStyle === 'compact'} />
          </div>
        ) : null}
        <div className="flex flex-col gap-6 lg:flex-row">
          {resultsCol}
          {mapCol}
        </div>
      </div>
    </section>
  );
}
